import type { CmsRendering } from '@eneco/cms-adapter';
import { object, string } from 'yup';
import type { AnyObject, ObjectSchema } from 'yup';

import type { FormFieldComponent, FormValues } from './types';

/**
 * Assemble a form's validation schema by walking its rendered children.
 *
 * Each field component declares its own rules via a static `formSchema`, so adding a field type
 * needs no change here — the same shape the legacy `genericFormSchema` used, but reading
 * `CmsRendering.content` (provider-neutral) instead of JSS `fields`.
 *
 * A child with no registered component, or one that declares no rules, contributes nothing rather
 * than blocking submission: an unmigrated field type should leave the rest of the form usable.
 */
export const buildFormSchema = (
  children: readonly CmsRendering[],
  components: Record<string, FormFieldComponent | undefined>,
): ObjectSchema<Partial<FormValues>, AnyObject> => {
  // Required for every form, regardless of which fields it holds.
  const fields: Record<string, unknown> = { recaptchaToken: string().required() };

  for (const child of children) {
    const rules = components[child.componentName]?.formSchema?.(child.content as Record<string, unknown>);
    if (rules) Object.assign(fields, rules);
  }

  return object().shape(fields as never) as ObjectSchema<Partial<FormValues>, AnyObject>;
};
